import axios, { AxiosError } from 'axios';
import { notifications } from '@mantine/notifications';
import { queryClient } from '@/config/queryClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface ApiError {
  detail: string;
  status_code: number;
  errors?: Record<string, string[]>;
}

export interface PaginationParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  search?: string;
}

// ---------------------------------------------------------------------------
// Axios instance
//
// Authentication is handled via httpOnly cookies set by the server.
// `withCredentials: true` ensures cookies are included on every request.
// No Authorization header injection is needed for browser clients.
// ---------------------------------------------------------------------------

const baseURL = import.meta.env.VITE_API_URL
  || (window.location.hostname === 'app.standardproduce.com'
    ? 'https://api.standardproduce.com/api/v1'
    : '/api/v1');

export const API_BASE_URL = baseURL;

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30_000,
});

// -- Request interceptor: attach Bearer token for dev (httpOnly cookies
//    don't work on localhost without HTTPS / __Host- prefix) ---
api.interceptors.request.use((config) => {
  const raw = localStorage.getItem('freshops-auth');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      const token = parsed?.state?.token;
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // ignore parse errors
    }
  }
  // Attach CSRF token from cookie
  const csrfMatch = document.cookie.match(/(?:^|;\s*)freshops_csrf=([^;]*)/);
  if (csrfMatch && config.headers) {
    config.headers['X-CSRF-Token'] = decodeURIComponent(csrfMatch[1]);
  }

  return config;
});

// -- Response interceptor: handle 401 + global error toasts ---
// Debounce connection error toasts so multiple simultaneous failures show only one.
let _connErrorTimer: ReturnType<typeof setTimeout> | null = null;

api.interceptors.response.use(
  (response) => {
    // Clear connection error debounce on any successful response
    if (_connErrorTimer) {
      clearTimeout(_connErrorTimer);
      _connErrorTimer = null;
    }
    return response;
  },
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401) {
      // Clear persisted user info and all cached data
      localStorage.removeItem('freshops-auth');
      queryClient.clear();
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    } else if (error.response && error.response.status !== 404 && error.response.status !== 405) {
      // Server error (non-401, non-404/405). 404/405 are silently handled by React Query.
      const rawDetail = error.response.data?.detail;
      const message = typeof rawDetail === 'string'
        ? rawDetail
        : Array.isArray(rawDetail)
          ? rawDetail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
          : rawDetail?.detail || rawDetail?.message || 'An unexpected error occurred';
      notifications.show({
        title: 'Error',
        message: String(message),
        color: 'red',
        autoClose: 5000,
      });
    } else if (error.request && !error.response) {
      // Network error — debounce so parallel failures produce one toast
      if (!_connErrorTimer) {
        _connErrorTimer = setTimeout(() => {
          _connErrorTimer = null;
          notifications.show({
            id: 'connection-error',
            title: 'Connection Error',
            message: 'Unable to reach the server. Please check your connection.',
            color: 'red',
            autoClose: 8000,
          });
        }, 300);
      }
    }
    return Promise.reject(error);
  },
);

export default api;
